import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RefreshCw } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import {
    atualizarPorCategorias, getPreviaCategorias, mensagemDeErro,
    type RotinaAtualizacaoCategorias, type RotinaGrupo,
} from '@/services/api/rotinas';
import { horaExibida, ordenarGrupos } from './rotinaUtils';

type Mudanca = RotinaAtualizacaoCategorias['grupos'][number];
type Bloco = { horas: string[]; entram: Mudanca['entram']; saem: Mudanca['saem']; ordem: number };

/** Grupos com exatamente a mesma mudança viram um bloco só: a lista não se repete. */
function agrupar(previa: RotinaAtualizacaoCategorias, grupos: RotinaGrupo[]): Bloco[] {
    const hora = new Map(grupos.map((g) => [g.id, horaExibida(g)]));
    const ordem = new Map(ordenarGrupos(grupos).map((g, i) => [g.id, i]));
    const blocos = new Map<string, Bloco>();
    for (const g of previa.grupos) {
        const ids = (l: { page_id: string }[]) => l.map((p) => p.page_id).sort().join(',');
        const chave = `${ids(g.entram)}|${ids(g.saem)}`;
        const b = blocos.get(chave) ?? { horas: [], entram: g.entram, saem: g.saem, ordem: ordem.get(g.grupo_id) ?? 0 };
        b.horas.push(hora.get(g.grupo_id) ?? g.hora_local.slice(0, 5));
        blocos.set(chave, b);
    }
    return [...blocos.values()].sort((a, b) => a.ordem - b.ordem);
}

/**
 * O gestor escolhe as categorias e todos os grupos ficam com as páginas ativas delas:
 * entram as que faltam, saem as de categoria desmarcada e as que ficaram inativas.
 * Abre marcado com o que os grupos já usam, então sem mexer nada é só "completar".
 */
export default function CategoriasDialog({ rotinaId, grupos, open, onOpenChange, onAtualizado }: {
    rotinaId: number;
    grupos: RotinaGrupo[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onAtualizado: () => void;
}) {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const [marcadas, setMarcadas] = useState<number[] | null>(null);

    useEffect(() => { if (!open) setMarcadas(null); }, [open]);

    const inicial = useQuery({
        queryKey: ['rotina-categorias', rotinaId],
        queryFn: () => getPreviaCategorias(rotinaId),
        enabled: open,
        staleTime: 0,
    });
    useEffect(() => {
        if (open && marcadas === null && inicial.data && !inicial.isFetching) {
            setMarcadas(inicial.data.categorias.filter((c) => c.grupos_que_usam > 0).map((c) => c.id));
        }
    }, [open, marcadas, inicial.data]);

    const escolha = useMemo(() => [...(marcadas ?? [])].sort((a, b) => a - b), [marcadas]);
    const previa = useQuery({
        queryKey: ['rotina-previa-categorias', rotinaId, escolha],
        queryFn: () => getPreviaCategorias(rotinaId, escolha),
        enabled: open && escolha.length > 0,
        placeholderData: keepPreviousData,
        staleTime: 0,
    });

    const aplicar = useMutation({
        mutationFn: () => atualizarPorCategorias(rotinaId, escolha),
        onSuccess: (r) => {
            // Sem isto, reabrir o modal mostra a prévia de antes da atualização.
            queryClient.removeQueries({ queryKey: ['rotina-categorias', rotinaId] });
            queryClient.removeQueries({ queryKey: ['rotina-previa-categorias', rotinaId] });
            toast({
                title: `${r.grupos.length} grupo(s) atualizado(s)`,
                description: 'Vale a partir da próxima materialização.',
            });
            onAtualizado();
            onOpenChange(false);
        },
        onError: (e) => toast({ variant: 'destructive', title: 'Não foi possível atualizar', description: mensagemDeErro(e, 'Tente de novo.') }),
    });

    const categorias = inicial.data?.categorias ?? [];
    const alternar = (id: number) =>
        setMarcadas((m) => (m ?? []).includes(id) ? (m ?? []).filter((x) => x !== id) : [...(m ?? []), id]);
    const blocos = previa.data && escolha.length > 0 ? agrupar(previa.data, grupos) : [];
    const mudam = escolha.length > 0 ? previa.data?.grupos.length ?? 0 : 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <RefreshCw className="h-5 w-5" /> Atualizar pelas categorias
                    </DialogTitle>
                    <DialogDescription>
                        Marque as categorias. Todos os grupos ficam com as páginas ativas delas: entram as que
                        faltam, saem as de categoria desmarcada e as que ficaram inativas.
                    </DialogDescription>
                </DialogHeader>

                {inicial.isLoading && (
                    <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Carregando as categorias…
                    </p>
                )}
                {inicial.isError && (
                    <p className="py-6 text-sm text-destructive">Não foi possível carregar as categorias.</p>
                )}

                {categorias.length > 0 && (
                    <div className="rounded-lg border">
                        <div className="flex items-center justify-between border-b bg-muted/40 px-3 py-2 text-xs">
                            <span><strong className="tabular-nums">{escolha.length}</strong> de {categorias.length} categorias</span>
                            <span className="flex gap-3">
                                <button type="button" className="underline underline-offset-2" onClick={() => setMarcadas(categorias.map((c) => c.id))}>
                                    Todas
                                </button>
                                <button type="button" className="underline underline-offset-2" onClick={() => setMarcadas([])}>
                                    Nenhuma
                                </button>
                            </span>
                        </div>
                        <ul className="grid max-h-56 grid-cols-1 overflow-y-auto py-1 text-sm sm:grid-cols-2">
                            {categorias.map((c) => (
                                <li key={c.id}>
                                    <label className="flex cursor-pointer items-center gap-2 px-3 py-1 hover:bg-muted/50">
                                        <Checkbox checked={escolha.includes(c.id)} onCheckedChange={() => alternar(c.id)} />
                                        <span className="flex-1 truncate">{c.nome}</span>
                                        <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{c.paginas}</span>
                                    </label>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {marcadas !== null && escolha.length === 0 && (
                    <p className="text-sm text-muted-foreground">Marque pelo menos uma categoria.</p>
                )}
                {escolha.length > 0 && previa.isFetching && !previa.data && (
                    <p className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" /> Calculando…
                    </p>
                )}
                {escolha.length > 0 && previa.data && mudam === 0 && !previa.isFetching && (
                    <p className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900">
                        Tudo em dia: os grupos já têm exatamente as páginas ativas dessas categorias.
                    </p>
                )}

                <div className={`space-y-2 ${previa.isFetching ? 'opacity-60' : ''}`}>
                    {blocos.map((b) => (
                        <details key={b.horas.join()} className="rounded-lg border">
                            <summary className="flex cursor-pointer items-center justify-between gap-2 bg-muted/40 px-3 py-2 text-sm">
                                <span>
                                    {b.horas.length === 1 ? 'Grupo das ' : 'Grupos das '}
                                    <span className="font-semibold tabular-nums">{b.horas.join(', ')}</span>
                                </span>
                                <span className="shrink-0 text-xs font-semibold tabular-nums">
                                    {b.entram.length > 0 && <span className="text-emerald-700">+{b.entram.length}</span>}
                                    {b.entram.length > 0 && b.saem.length > 0 && ' / '}
                                    {b.saem.length > 0 && <span className="text-destructive">−{b.saem.length}</span>}
                                    {b.horas.length > 1 ? ' em cada' : ''}
                                </span>
                            </summary>
                            <ul className="max-h-60 divide-y overflow-y-auto text-sm">
                                {b.entram.map((p) => (
                                    <li key={`+${p.page_id}`} className="flex items-center justify-between gap-3 px-3 py-1.5">
                                        <span className="truncate">
                                            <span className="mr-1.5 font-semibold text-emerald-700">+</span>
                                            {p.page_name || <em className="text-muted-foreground">sem nome</em>}
                                        </span>
                                        <span className="shrink-0 text-xs text-muted-foreground">{p.categoria}</span>
                                    </li>
                                ))}
                                {b.saem.map((p) => (
                                    <li key={`-${p.page_id}`} className="flex items-center justify-between gap-3 px-3 py-1.5">
                                        <span className="truncate">
                                            <span className="mr-1.5 font-semibold text-destructive">−</span>
                                            {p.page_name || <span className="font-mono text-xs">{p.page_id}</span>}
                                        </span>
                                        <span className="shrink-0 text-xs text-muted-foreground">{p.motivo}</span>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    ))}
                </div>

                {escolha.length > 0 && !!previa.data?.em_outra_rotina && (
                    <p className="text-xs text-muted-foreground">
                        {previa.data.em_outra_rotina} página(s) não entram por já estarem em outra Rotina ligada neste dia.
                    </p>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>Fechar</Button>
                    <Button
                        disabled={mudam === 0 || previa.isFetching || aplicar.isPending}
                        onClick={() => aplicar.mutate()}
                    >
                        {aplicar.isPending ? 'Atualizando…' : `Atualizar ${mudam} grupo(s)`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
